/**
 * Segmentation utility — labels, colours, icons, formatters.
 * Import from this file throughout all segmentation components.
 *
 * Sections in this file:
 *   1. Segments (order, labels, colours, icons)
 *   2. Segment categories (New / High Engagement / Active / Re-engagement)
 *   3. Identifiers (linked identifiers display)
 *   4. Churn risk
 *   5. Channels
 *   6. Demographic promotion (inferred -> declared field mapping)
 *   7. Demographic charts
 *   8. AI credits
 *   9. Formatters
 *   10. AI insights
 *   11. Misc
 */

// ============================================================
// 1. Segments
// ============================================================

/** Canonical segment order for display — most valuable to least active. */
export const SEGMENT_ORDER = [
  'new_listener',
  'super_fan',
  'show_loyalist',
  'competition_chaser',
  'occasional_engager',
  'at_risk',
  'dormant',
];

/** Human-readable label for each segment key. */
export const SEGMENT_LABELS = {
  new_listener: 'New Listener',
  super_fan: 'Super Fan',
  show_loyalist: 'Show Loyalist',
  competition_chaser: 'Competition Chaser',
  occasional_engager: 'Occasional Engager',
  at_risk: 'At Risk',
  dormant: 'Dormant',
};

/** Colony brand colour per segment. */
export const SEGMENT_COLOURS = {
  new_listener: 'rgb(45, 147, 207)',
  super_fan: 'rgb(104, 48, 144)',
  show_loyalist: 'rgb(20, 179, 147)',
  competition_chaser: 'rgb(242, 148, 33)',
  occasional_engager: 'rgb(52, 191, 194)',
  at_risk: 'rgb(238, 36, 101)',
  dormant: 'rgb(180, 180, 180)',
};

/** Font Awesome icon class per segment. */
export const SEGMENT_ICONS = {
  new_listener: 'fa-star-o',
  super_fan: 'fa-star',
  show_loyalist: 'fa-headphones',
  competition_chaser: 'fa-trophy',
  occasional_engager: 'fa-hand-o-right',
  at_risk: 'fa-exclamation-triangle',
  dormant: 'fa-moon-o',
};

/**
 * @param {string} key
 * @returns {string}  human-readable label for a segment key
 */
export const getSegmentLabel = key => SEGMENT_LABELS[key] || key;

/**
 * @param {string} key
 * @returns {string}  Colony brand colour for a segment key
 */
export const getSegmentColour = key => SEGMENT_COLOURS[key] || 'rgb(180,180,180)';

/**
 * @param {string} key
 * @returns {string}  Font Awesome icon class for a segment key
 */
export const getSegmentIcon = key => SEGMENT_ICONS[key] || 'fa-users';

// ============================================================
// 2. Segment categories (New / High Engagement / Active / Re-engagement)
// ============================================================

/**
 * Colony brand colour per segment category.
 * Used for the "New", "High Engagement", "Active", and "Re-engagement" cards.
 */
const CATEGORY_COLOURS = {
  New: 'rgb(45, 147, 207)',
  'High Engagement': 'rgb(20, 179, 147)',
  Active: 'rgb(242, 148, 33)',
  'Re-engagement': 'rgb(238, 36, 101)',
};

/** Brief explanation shown in the category badge tooltip. */
const CATEGORY_EXPLANATIONS = {
  New: 'Recently joined — still building an engagement profile',
  'High Engagement': 'Your most valuable, consistently active listeners',
  Active: 'Engaged, but without a strong or predictable pattern',
  'Re-engagement': 'Declining or inactive — needs attention to win back',
};

/**
 * @param {string} category
 * @returns {string}
 */
export const getCategoryColour = category => CATEGORY_COLOURS[category] || 'rgb(153,153,153)';

/**
 * @param {string} category
 * @returns {string}
 */
export const getCategoryExplanation = category => CATEGORY_EXPLANATIONS[category] || '';

// ============================================================
// 3. Identifiers (linked identifiers display)
// ============================================================

/** Font Awesome icon class per identifier key. */
export const IDENTIFIER_ICONS = {
  mobile_number: 'fa-mobile',
  email: 'fa-envelope-o',
  instagram_handle: 'fa-instagram',
  facebook_handle: 'fa-facebook',
  twitter_handle: 'fa-twitter',
  x_handle: 'fa-twitter',
  telegram_handle: 'fa-telegram',
  whatsapp_handle: 'fa-whatsapp',
  threads_handle: 'fa-at',
  social_handle: 'fa-at',
  id_number: 'fa-id-card-o',
};

/** Human-readable label for each identifier key. */
export const IDENTIFIER_LABELS = {
  mobile_number: 'Mobile',
  email: 'Email',
  instagram_handle: 'Instagram',
  facebook_handle: 'Facebook',
  twitter_handle: 'Twitter',
  x_handle: 'X',
  telegram_handle: 'Telegram',
  whatsapp_handle: 'WhatsApp',
  threads_handle: 'Threads',
  social_handle: 'Social handle',
  id_number: 'ID number',
};

/**
 * @param {string} type
 * @returns {string}  Font Awesome icon class for an identifier key
 */
export const getIdentifierIcon = type => IDENTIFIER_ICONS[type] || 'fa-link';

/**
 * @param {string} type
 * @returns {string}  human-readable label for an identifier key
 */
export const getIdentifierLabel = type => IDENTIFIER_LABELS[type] || type;

// ============================================================
// 4. Churn risk
// ============================================================

/** Colony brand colour per churn risk level. */
export const CHURN_RISK_COLOURS = {
  low: 'rgb(20, 179, 147)',
  medium: 'rgb(242, 148, 33)',
  high: 'rgb(238, 36, 101)',
};

/**
 * @param {string} risk
 * @returns {string}  Colony brand colour for a churn risk level
 */
export const getChurnColour = risk => CHURN_RISK_COLOURS[risk] || '#ccc';

// ============================================================
// 5. Channels
// ============================================================

/**
 * Display name per channel type integer.
 * Matches primary_channel_type values stored in MongoDB.
 */
export const CHANNEL_NAMES = {
  0: 'SMS',
  1: 'Email',
  2: 'Twitter',
  3: 'Facebook',
  8: 'USSD',
  9: 'WhatsApp',
  10: 'Instagram',
  12: 'Telegram',
  19: 'Survey',
  20: 'Import',
  22: 'TikTok',
};

/**
 * SocialIcon component type string per channel type integer.
 * Used with <social-icon :type="..." /> throughout the dashboard.
 */
export const CHANNEL_SOCIAL_ICON_TYPE = {
  0: 'SMS',
  1: 'Email',
  2: 'Twitter',
  3: 'Facebook',
  8: 'USSD',
  9: 'WhatsApp',
  10: 'Instagram',
  12: 'Telegram',
  19: 'Survey',
  20: 'Import',
  22: 'TikTok',
};

/**
 * @param {number} type
 * @returns {string}  display name for a channel type integer
 */
export const getChannelName = type => CHANNEL_NAMES[type] || `Channel ${type}`;

/**
 * @param {number} type
 * @returns {string}  SocialIcon type string for a channel type integer
 */
export const getChannelIconType = type => CHANNEL_SOCIAL_ICON_TYPE[type] || 'SMS';

// ============================================================
// 6. Demographic promotion (inferred -> declared field mapping)
// ============================================================

/**
 * Maps a camelCase inferred-field name (as used in Vue, e.g. in
 * ListenerDialogAI's inferredRows) to the snake_case name the
 * promote-demographics backend expects.
 */
const DEMOGRAPHIC_FIELD_MAP = {
  gender: 'gender',
  ageGroup: 'age_group',
  location: 'location',
  language: 'language',
  ethnicity: 'ethnicity',
  incomeBracket: 'income_bracket',
  education: 'education',
  employmentStatus: 'employment_status',
  maritalStatus: 'marital_status',
  occupation: 'occupation',
  lifeStage: 'life_stage',
};

/**
 * Converts a camelCase inferred-field name to the snake_case name the
 * promote-demographics backend expects. Falls back to the field name
 * unchanged if it isn't in the map, so a typo or new unregistered
 * field still reaches the backend (which will reject it with a clear
 * error) rather than silently sending undefined.
 *
 * @param {string|null} field  camelCase field name, or null for bulk promote
 * @returns {string|null}      snake_case field name, or null unchanged
 */
export const toPromoteField = field => {
  if (!field) {
    return null;
  }
  return DEMOGRAPHIC_FIELD_MAP[field] || field;
};

// ============================================================
// 7. Demographic charts
// ============================================================

/**
 * Ordered colour palette for demographic charts (gender, age, language, location).
 * Last entry (grey) is reserved for the "Other" bucket.
 */
export const DEMOGRAPHIC_COLOURS = [
  'rgb(104,48,144)',
  'rgb(45,147,207)',
  'rgb(20,179,147)',
  'rgb(242,148,33)',
  'rgb(238,36,101)',
  'rgb(52,191,194)',
  'rgb(180,180,180)',
];

/**
 * Picks a colour for one demographic key by index.
 * "Other", "other", or empty keys always get the grey fallback.
 *
 * @param {string} k
 * @param {number} i
 * @returns {string}
 */
const colourForKey = (k, i) => {
  if (k === 'Other' || k === 'other' || !k) {
    return 'rgb(180,180,180)';
  }
  return DEMOGRAPHIC_COLOURS[i % (DEMOGRAPHIC_COLOURS.length - 1)];
};

/**
 * Maps an array of demographic keys to colours.
 *
 * @param {string[]} keys
 * @returns {string[]}
 */
export const getDemographicColours = keys => keys.map((k, i) => colourForKey(k, i));

// ============================================================
// 8. AI credits
// ============================================================

/** Error code returned when AI credits are exhausted. */
export const CREDITS_EXHAUSTED = 'credits_exhausted';

/**
 * @param {Error} e  an Apollo error from a failed AI mutation/query
 * @returns {boolean}  true if the failure was specifically credits exhaustion
 */
export const isCreditsError = e => {
  const gqlErrors = (e && e.graphQLErrors) || [];
  return gqlErrors.some(
    err =>
      err.message === CREDITS_EXHAUSTED ||
      (err.extensions && err.extensions.code === CREDITS_EXHAUSTED),
  );
};

/**
 * The service's own message when a part of the product has used its monthly
 * limit (for example "Monthly limit reached for Bulk enrichment (500
 * credits). It resets on the 1st."), or '' for any other error.
 *
 * Note: Only that one part is blocked, so this is not a credits error and
 * the rest of AI keeps working.
 *
 * @param {Error} e  an Apollo error from a failed AI mutation/query
 * @returns {string}
 */
export const featureLimitMessage = e => {
  const gqlErrors = (e && e.graphQLErrors) || [];
  const hit = gqlErrors.find(err => /^Monthly limit reached/.test(err.message || ''));
  return hit ? hit.message : '';
};

// ============================================================
// 9. Formatters
// ============================================================

/**
 * Formats a recency value (days since last interaction) as a human-readable string.
 * e.g. 0 -> 'Today', 1 -> 'Yesterday', 5 -> '5d ago', 14 -> '2w ago'
 *
 * @param {number|null} days
 * @returns {string}
 */
export const formatRecency = days => {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/**
 * Formats a number for compact display.
 * e.g. 1500 -> '1.5K', 2300000 -> '2.3M', 0 -> '0'
 *
 * @param {number|null} n
 * @returns {string}
 */
export const formatNumber = n => {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

/**
 * Share of a total as text without the % sign. A group that has members
 * never reads as 0: below 0.01% it shows "<0.01".
 * @param {number} count
 * @param {number} total
 * @returns {string}
 */
export const formatPercent = (count, total) => {
  if (!total || !count) {
    return '0';
  }
  const pct = (count / total) * 100;
  if (pct < 0.01) {
    return '<0.01';
  }
  if (pct < 1) {
    return pct.toFixed(2);
  }
  return pct < 10 ? pct.toFixed(1) : pct.toFixed(0);
};

/**
 * Capitalises the first letter of each word in a snake_case or lowercase string.
 * e.g. 'drive_time' -> 'Drive Time', 'afternoon' -> 'Afternoon'
 *
 * @param {string|null} val
 * @returns {string}
 */
export const formatLabel = val => {
  if (!val) {
    return '—';
  }
  return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Returns a CSS class for colouring recency values.
 * Green (navy) for recent, orange for moderate, red for stale.
 *
 * @param {number} days
 * @returns {string}
 */
export const recencyClass = days => {
  if (days <= 7) return 'text-navy';
  if (days <= 30) return 'text-warning';
  if (days <= 60) return 'text-warning';
  return 'text-danger';
};

// ============================================================
// 10. AI insights
// ============================================================

/**
 * Highlights numbers and known channel names in AI-generated text,
 * wrapping them in <strong> so key facts stand out visually without
 * relying on the LLM to format its own output consistently.
 *
 * @param {string} text
 * @returns {string}
 */
export const highlightInsightText = text => {
  if (!text) {
    return text;
  }

  let result = text;

  // Numbers (including decimals, commas as thousand separators, %)
  result = result.replace(/\b\d{1,3}(,\d{3})*(\.\d+)?%?\b/g, match => `<strong>${match}</strong>`);

  // Known channel names
  Object.values(CHANNEL_NAMES).forEach(channel => {
    const re = new RegExp(`\\b${channel}\\b`, 'g');
    result = result.replace(re, `<strong>${channel}</strong>`);
  });

  return result;
};

// ============================================================
// 11. Misc
// ============================================================

/**
 * Picks a value from an object trying snake_case first, then camelCase.
 * Falls back to fallback (default '—') if neither key exists.
 * Useful for normalising API responses that may return either casing.
 *
 * @param {Object} obj
 * @param {string} snake   e.g. 'best_time'
 * @param {string} camel   e.g. 'bestTime'
 * @param {*}      fallback
 * @returns {*}
 */
export const pick = (obj, snake, camel, fallback) =>
  obj[snake] || obj[camel] || (fallback !== undefined ? fallback : '—');

// ============================================================
// 12. Profile images
// ============================================================

/**
 * True when a GraphQL error is the segmentation service saying the
 * listener has no profile under that identifier (for example a Facebook
 * listener whose "originator" is a display name rather than an account id).
 * @param {Error} e
 * @returns {boolean}
 */
export const isNotFoundError = e => {
  const gqlErrors = (e && e.graphQLErrors) || [];
  return gqlErrors.some(err => /not found/i.test(err.message || ''));
};

/**
 * True when a stored profile image value is worth handing to the browser.
 * Profile image URLs come from social platforms and are never guaranteed to
 * still resolve (CDN URLs expire, accounts change), so the component also
 * handles the load failing — this only filters out values that can never
 * load, avoiding a pointless request and the error flash that follows.
 *
 * @param {*} value  listener.profileImage
 * @returns {boolean}
 */
export const isLoadableImageUrl = value =>
  typeof value === 'string' && /^https?:\/\/\S+$/i.test(value.trim());

// ============================================================
// 13. Listener paging
// ============================================================

/** Listener cards shown per page in the browser. */
export const LISTENERS_PER_PAGE = 12;

/** UI pages' worth of listeners fetched from the API in one request. */
export const PAGES_PER_FETCH = 8;

/**
 * Listeners requested per API call. A whole multiple of LISTENERS_PER_PAGE,
 * so every fetched batch fills complete pages — a batch that ended mid-page
 * showed a short page that silently grew when the next batch arrived.
 */
export const LISTENER_FETCH_SIZE = LISTENERS_PER_PAGE * PAGES_PER_FETCH;

// ============================================================
// 14. Similar listeners — what the matches have in common
// ============================================================

const lower = value =>
  String(value)
    .trim()
    .toLowerCase();

/**
 * Splits one match's interests into the ones it shares with the reference
 * listener (shown first, highlighted) and the rest.
 * @param {string[]} interests  the match's inferred interests
 * @param {string[]} reference  the reference listener's inferred interests
 * @returns {{label: string, shared: boolean}[]}
 */
export const tagInterests = (interests, reference) => {
  const own = new Set((reference || []).map(lower));
  const tagged = (interests || []).filter(Boolean).map(label => ({
    label,
    shared: own.has(lower(label)),
  }));
  return [...tagged.filter(t => t.shared), ...tagged.filter(t => !t.shared)];
};

/**
 * Counts how many of the matches carry each value (interests, location,
 * age group, gender, segment), most common first. `enriched` is how many
 * matches have interests at all, so "3 of 4" is not mistaken for "3 of 20".
 * @param {Object[]} matches  similar listeners from the API
 * @param {number} top        how many values to keep per list
 */
export const summariseSimilar = (matches, top = 5) => {
  const list = matches || [];

  const count = select => {
    const tally = {};
    list.forEach(m => {
      const values = [].concat(select(m) || []);
      // a listener counts once per value, however often it is repeated
      new Set(values.filter(Boolean).map(v => String(v).trim())).forEach(v => {
        const key = lower(v);
        tally[key] = tally[key] || { label: v, count: 0 };
        tally[key].count += 1;
      });
    });
    return Object.values(tally)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .slice(0, top)
      .map(item => ({ ...item, pct: Math.round((item.count / list.length) * 100) }));
  };

  return {
    total: list.length,
    enriched: list.filter(m => (m.inferred_interests || []).length > 0).length,
    interests: count(m => m.inferred_interests),
    locations: count(m => m.location),
    ageGroups: count(m => m.age_group || m.inferred_age_group),
    genders: count(m => m.gender || m.inferred_gender),
    segments: count(m => m.current_segment),
  };
};



// WEBPACK FOOTER //
// ./src/components/segmentation/utils/segmentation.js